import styled from '@emotion/styled';
import {media, spacing, Visible} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {NavItem} from 'UI/components/action/NavItem';

import {MenuBase, MenuTransitions} from 'UI/animations/header';

import {useMenu} from './useMenu';
import {BurgerMenu} from 'Images/icons';

import type {Header} from 'types/payload-types';


/**
 * The props for the `Menu` component, which renders a list of navigation links within the site header.
 * This interface defines the structure expected by the `Menu` component. It requires a `navigation` array containing
 * structured navigation entries, each typically including an `id` and a `navigationLink` object. Additionally, it
 * allows specifying an optional `testId` string to support precise targeting in test automation setups by assigning
 * it to a `data-cy` attribute on the root menu wrapper.
 */
interface ComponentProps {
    /**
     * An array of navigation entries used to render the main menu. Each entry usually includes an `id` and
     * a `navigationLink` containing link configuration.
     */
    navigation: Header['navigation'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Menu'
     */
    testId?: string;
}

/**
 * A navigation menu component that renders a list of navigation links from provided data.
 * This component is responsible for rendering the main navigation inside the header. It accepts a `navigation`
 * array consisting of navigation link entries and delegates the rendering of each link to the `NavItem` component.
 * Each link is wrapped in a styled `Item` container. It supports test automation by exposing a customizable
 * `testId` as a `data-cy` attribute on the root element.
 * The component is wrapped in MobX's `observer` to ensure reactive rendering when observable data changes.
 * Accessibility is ensured by setting an appropriate `aria-label` for screen readers.
 *
 * @param props            The component props.
 * @param props.navigation An array of navigation entries, each containing an `id` and a `navigationLink` object.
 * @param props.testId     An optional test ID used for automation selectors. Defaults to `'Menu'`.
 * @returns A JSX element that renders the main navigation menu.
 *
 * @example
 * ```tsx
 * <Menu navigation={header.navigation} />
 * ```
 */
const Menu = observer(({navigation, testId = 'Menu'}: ComponentProps) => {
    const {handleClick, handleToggle, initial, transition} = useMenu();

    return (
        <Wrapper animate={transition} initial={initial} variants={MenuBase}>
            <Visible isLoadingHtml md sm xs>
                <StyledButton
                    aria-controls="mainMenu"
                    aria-expanded={transition === 'open'}
                    aria-haspopup="true"
                    aria-label="Hauptmenü öffnen"
                    as="button"
                    variant="icon-only"
                    onClick={handleToggle}
                >
                    {/* @ts-expect-error */}
                    <BurgerMenu height={48} width={48} />
                </StyledButton>
            </Visible>
            <MenuWrapper
                aria-label="Hauptnavigation"
                data-cy={testId}
                id="mainMenu"
                variants={MenuTransitions}
            >
                {navigation?.map(({id, navigationLink}) => (
                    <Item key={id}>
                        <NavItem key={id} link={navigationLink} testId={`HeaderNavItem-${id}`} onClick={handleClick} />
                    </Item>
                ))}
            </MenuWrapper>
        </Wrapper>
    );
});

Menu.displayName = 'Menu';

export {Menu};

const Wrapper = styled(motion.div)``;

const StyledButton = styled(Button)`
    position: relative;
    z-index: 30;
`;

const MenuWrapper = styled(motion.ul)`
    background: ${({theme}) => theme.colors.backgroundLight};
    box-shadow: ${({theme}) => theme.boxShadows.menuShadow};
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    height: 100vh;
    list-style: none;
    margin: 0;
    max-width: fit-content;
    min-width: 26.9rem;
    padding: 0;
    padding-block-start: ${spacing(30)};
    position: absolute;
    right: 0;
    top: 0;
    transform: translateX(120%);

    ${media('lg')} {
        background: initial;
        box-shadow: initial;
        height: initial;
        flex-direction: row;
        gap: ${spacing(6)};
        padding: 0;
        position: relative;
        transform: translateX(0%);
    }

    ${media('xl')} {
        gap: ${spacing(10)};
    }
`;

const Item = styled.li`
    height: 4.8rem;
    line-height: 1.6rem;
    width: 100%;

    ${media('lg')} {
        height: 1.6rem;
        line-height: 1.6rem;
        width: initial;
    }
`;